import Page from "components/Page";
import React, { useState, useEffect, Suspense, useCallback } from "react";
import { withTranslation } from "react-i18next";
import styles from "./Scheduler.module.scss";
import "./Scheduler.scss";
import { TabContent, TabPane, Nav, NavItem, NavLink } from "reactstrap";
import Card from "components/Card";
import QuestimIcon from "assets/images/question_mark.svg";
import AboutSchedulerModal from "./components/Modals/AboutSchedulerModal";
import Loader from "components/Loader";
import constants from "../../../constants";
import useAgendaFilter from "hooks/useAgendaFilter";
import useStaffAvailabilityFilter from "hooks/useStaffAvailabilityFilter";
import { encodeId, getStorage } from "utils";
import { useHistory } from "react-router-dom";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import {
  SCHEDULER_MAIN_TAB,
  SCHEDULER_TABS_STORAGE_KEY,
  readSchedulerTabState,
  withActiveMainTab,
  withOfficeSubTab,
} from "accountOwner/pages/Scheduler/schedulerTabState";
const YourScheduleTab = React.lazy(() => import("././YourScheduleTab"));
const OfficeSchedulerTab = React.lazy(() => import("./OfficeSchedulerTab"));

/**
 * Resolves the back navigation route based on entry context.
 * Keeps navigation rules centralized and easier to maintain.
 */
const resolveBackRoute = ({
  isFromOwnerOfficeModule,
  isFromLeaveDetail,
  isChatFromDashBoard,
}) => {
  if (isFromOwnerOfficeModule?.officeSlection) {
    return {
      path: constants.routes.accountOwner.officeOptions,
      id: isFromOwnerOfficeModule.officeId,
    };
  }
  if (isFromOwnerOfficeModule?.isAdminControl) {
    return {
      path: constants.routes.staff.officeAdmin,
      id: isFromOwnerOfficeModule.officeId,
    };
  }
  if (isFromOwnerOfficeModule?.staffOfficeSlection) {
    return {
      path: constants.routes.staff.officeOptions,
      id: isFromOwnerOfficeModule.officeId,
    };
  }
  if (isFromLeaveDetail?.isFromleaveConflict) {
    return {
      path: constants.routes.staff.leaves,
      id: isFromLeaveDetail?.officeId,
    };
  }
  if (isChatFromDashBoard) {
    return { absolute: constants.routes.dashboard.managerDashboard };
  }
  return { absolute: "/" };
};

const Scheduler = ({ t }) => {
  const [subTabs, setSubTabs] = useState(readSchedulerTabState);
  const history = useHistory();
  const isChatFromDashBoard = getStorage("isChatFromDashBoard");
  const isFromLeaveDetail = getStorage("IsFromLeaveSchedule");
  const isFromOwnerOfficeModule = getStorage("isFromOwnerOfficeModule");

  useEffect(() => {
    localStorage.setItem(SCHEDULER_TABS_STORAGE_KEY, JSON.stringify(subTabs));
  }, [subTabs]);

  const [activeTab, setActiveTab] = useState(subTabs.tab);
  const agendaFiltersData = useAgendaFilter();
  const staffAvailFiltersData = useStaffAvailabilityFilter();

  const handleMainTabChange = useCallback((nextTab) => {
    setActiveTab(nextTab);
    setSubTabs((prev) => withActiveMainTab(prev, nextTab));
  }, []);

  useEffect(() => {
    if (isFromLeaveDetail?.isFromleaveConflict) {
      setActiveTab(SCHEDULER_MAIN_TAB.OFFICE);
      setSubTabs((prev) => withOfficeSubTab(prev, "1"));
    }
  }, [isFromLeaveDetail?.isFromleaveConflict]);

  const [aboutSchedulerModal, setAboutSchedulerModal] = useState(false);
  const {
    ownerList,
    commonOwnerData: selectedOwner,
    effectiveOwnerData,
    ownerListLoading,
    isStaff,
    subscriptionValues: { isSchedulerFeatureEnabled },
    subscriptionDetails,
    isOwner,
  } = useAccountOwnerCommon();

  useEffect(() => {
    if (
      subscriptionDetails &&
      selectedOwner &&
      !isSchedulerFeatureEnabled &&
      ((isStaff && ownerList?.length === 1) || isOwner)
    ) {
      window.location.href = "/";
    }
  }, [selectedOwner, isSchedulerFeatureEnabled]);

  const goToPreviousScreen = () => {
    const route = resolveBackRoute({
      isFromOwnerOfficeModule,
      isFromLeaveDetail,
      isChatFromDashBoard,
    });
    if (route.absolute) {
      history.push(route.absolute);
      return;
    }
    history.push({
      pathname: route.path.replace(":officeId", encodeId(route.id)),
    });
  };
  const isNotFromleaveConflict = !isFromLeaveDetail?.isFromleaveConflict;
  return (
    <Page onBack={goToPreviousScreen} className="scheduler-page">
      {ownerListLoading && <Loader />}
      <div className="d-flex justify-content-between page_title_margin_top marginBottom30 align-items-center">
        <div className="d-flex flex-column align-items-start">
          <h2 className="page-title mb-0 margin-fix">
            {isNotFromleaveConflict
              ? t("accountOwner.scheduler")
              : "Staff Schedule"}{" "}
          </h2>
          {isStaff &&
            isNotFromleaveConflict &&
            effectiveOwnerData?.id && (
            <div className="show-owner-name-text d-none d-md-block">
              {t("staff.showingSchedule")}{" "}
              <span>
                '{effectiveOwnerData.firstName} {effectiveOwnerData.lastName}'
              </span>{" "}
              {t("dashBoard.office")}
            </div>
          )}
        </div>
        <div className="top-right-text d-flex gap-sm">
          {isNotFromleaveConflict && (
            <button
              type="button"
              className={styles.question_btn + " cfs-sm"}
              onClick={() => setAboutSchedulerModal(true)}
            >
              <img src={QuestimIcon} alt="QuestimIcon" />
              {t("navbar.help")}
            </button>
          )}
        </div>
      </div>
      {isStaff && isNotFromleaveConflict && effectiveOwnerData?.id && (
        <>
          <div className="show-owner-name-text marginBottom30 d-block d-md-none gap-sm">
            {t("staff.showingSchedule")}{" "}
            <span>
              '{effectiveOwnerData.firstName} {effectiveOwnerData.lastName}'
            </span>{" "}
            {t("dashBoard.office")}
          </div>
        </>
      )}
      <Card
        className={`${styles["scheduler-card"]} ${isFromLeaveDetail?.isFromleaveConflict ? styles["schedular-card-page-box"] : ""}`}
      >
        <div className="common-tabs scheduler-tabs">
          {isNotFromleaveConflict && (
            <Nav tabs>
              <NavItem>
                <NavLink
                  className={activeTab == "1" ? "active" : ""}
                  onClick={() => handleMainTabChange(SCHEDULER_MAIN_TAB.OFFICE)}
                >
                  {t("accountOwner.officeScheduler")}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab == "2" ? "active" : ""}
                  onClick={() =>
                    handleMainTabChange(SCHEDULER_MAIN_TAB.MY_SCHEDULE)
                  }
                >
                  {t("accountOwner.mySchedule")}
                </NavLink>
              </NavItem>
            </Nav>
          )}
          <TabContent activeTab={activeTab}>
            <Suspense fallback={<Loader />}>
              <TabPane tabId="1">
                {activeTab === "1" && (
                  <OfficeSchedulerTab
                    subTabed={subTabs}
                    setSubTabed={setSubTabs}
                    staffAvailFiltersData={staffAvailFiltersData}
                  />
                )}
              </TabPane>
              <TabPane tabId="2">
                {activeTab === "2" && (
                  <YourScheduleTab
                    subTabed={subTabs}
                    setSubTabed={setSubTabs}
                    agendaFiltersData={agendaFiltersData}
                  />
                )}
              </TabPane>
            </Suspense>
          </TabContent>
        </div>
      </Card>
      <AboutSchedulerModal
        showSwitchOwnerModal={aboutSchedulerModal}
        setShowSwitchOwnerModal={setAboutSchedulerModal}
      />
    </Page>
  );
};

export default withTranslation()(Scheduler);
